import { initLogger } from '../../../logger';
import { ALTERNATIVE_KEYWORDS, declaresOptions } from '../../../spectral/functions/pattern/declaration-paths';
function getItemsInOptionsRelationship(optionsRelationship) {
    return optionsRelationship['properties']['relationship-type']['properties']['options']['prefixItems'];
}
function extractOptionsFromBlock(optionsRelationship, blockType) {
    return getItemsInOptionsRelationship(optionsRelationship)
        .filter((prefixItem) => blockType in prefixItem)
        .map((prefixItem) => prefixItem[blockType])
        .map((choices) => ({
        optionType: blockType,
        optionId: optionsRelationship['properties']['unique-id']['const'],
        prompt: optionsRelationship['properties']['description']['const'],
        choices: choices.map(choice => ({
            description: choice['properties']['description']['const'],
            nodes: choice['properties']['nodes']['const'],
            relationships: choice['properties']['relationships']['const']
        }))
    }));
}
/**
 * Gets the relationships prefixItems from a pattern, handling allOf structures.
 * @param pattern - The pattern object
 * @returns The prefixItems array from relationships, or empty array if not found
 */
function getRelationshipsPrefixItems(pattern) {
    // Direct access for standard patterns
    if (pattern['properties']?.['relationships']?.['prefixItems']) {
        return pattern['properties']['relationships']['prefixItems'];
    }
    // Handle allOf patterns - look for relationships in each allOf schema
    if (pattern['allOf'] && Array.isArray(pattern['allOf'])) {
        for (const schema of pattern['allOf']) {
            if (schema['properties']?.['relationships']?.['prefixItems']) {
                return schema['properties']['relationships']['prefixItems'];
            }
        }
    }
    return [];
}
/**
 * Extracts the potential choices that a user can make from a pattern
 * @param pattern - The pattern to extract options from
 * @param debug - Whether to enable debug logging
 * @returns A list of options that the user can choose from
 */
export function extractOptions(pattern, debug = false) {
    const logger = initLogger(debug, 'calm-generate-options');
    const calmItems = getRelationshipsPrefixItems(pattern);
    if (calmItems.length === 0) {
        logger.debug('No relationship prefixItems found in pattern');
        return [];
    }
    const options = calmItems
        .filter((rel) => declaresOptions(rel))
        .flatMap((optionsRel) => [
        ...extractOptionsFromBlock(optionsRel, 'oneOf'),
        ...extractOptionsFromBlock(optionsRel, 'anyOf')
    ]);
    logger.debug(`Found the following options in the pattern: ${options}`);
    return options;
}
function alternativesOf(item) {
    const keyword = ALTERNATIVE_KEYWORDS.find(name => item?.[name]);
    return keyword ? item?.[keyword] : undefined;
}
function flattenOneOfAndAnyOf(item, selectionPredicate) {
    const alternatives = alternativesOf(item);
    if (!alternatives) {
        return [item];
    }
    return alternatives.filter((x) => selectionPredicate(x));
}
/**
 * Selected `items` members are promoted into `prefixItems`, so the instantiator only ever
 * reads positions. The `items` block itself stays, because validation compiles this same
 * pattern and needs it to keep constraining whatever the architecture adds.
 */
function selectDeclarations(pattern, calmType, ids) {
    const declarations = pattern['properties'][calmType];
    if (!declarations) {
        return;
    }
    const selectionPredicate = (x) => ids.includes(x['properties']['unique-id']['const']);
    const catalogue = alternativesOf(declarations['items']) ? [declarations['items']] : [];
    declarations['prefixItems'] = [...(declarations['prefixItems'] ?? []), ...catalogue]
        .flatMap((item) => flattenOneOfAndAnyOf(item, selectionPredicate));
}
function flattenOptionsRelationship(relationship, choices) {
    if (!declaresOptions(relationship)) {
        return relationship;
    }
    const selectionPredicate = (x) => choices.map(choice => choice.description).includes(x['properties']['description']['const']);
    const newItems = getItemsInOptionsRelationship(relationship)
        .flatMap((item) => flattenOneOfAndAnyOf(item, selectionPredicate));
    relationship['properties']['relationship-type']['properties']['options']['prefixItems'] = newItems;
    return relationship;
}
function flattenOptionsRelationships(pattern, choices) {
    const relationships = pattern['properties']['relationships'];
    if (!relationships?.['prefixItems']) {
        return;
    }
    relationships['prefixItems'] = relationships['prefixItems']
        .map((rel) => flattenOptionsRelationship(rel, choices));
}
/**
 * Selects the choices from the pattern and removes all other choices.
 * @param inputPattern - The input pattern to select choices from
 * @param choices - The choices to select
 * @param debug - Whether to enable debug logging
 * @returns A new pattern object with the selected choices and all oneOf and anyOf blocks flattened
 */
export function selectChoices(inputPattern, choices, debug = false) {
    const logger = initLogger(debug, 'calm-generate-options');
    logger.debug(`Selecting these choices from the pattern [${JSON.stringify(choices)}]`);
    const pattern = structuredClone(inputPattern); // deep copy so we don't mutate the input pattern, which may be cached and reused by callers
    const nodeIds = choices.flatMap(choice => choice.nodes);
    const relationshipIds = choices.flatMap(choice => choice.relationships);
    selectDeclarations(pattern, 'nodes', nodeIds);
    selectDeclarations(pattern, 'relationships', relationshipIds);
    flattenOptionsRelationships(pattern, choices);
    logger.debug(`Pattern with all non chosen choices removed: [${JSON.stringify(pattern)}]`);
    return pattern;
}
//# sourceMappingURL=options.js.map